import { Button, cn, SegmentedControl } from '@up2stay/up2promo-ui';
import { Bell, Plus } from 'lucide-react';

import type { DashboardRangeDays } from './dashboard-status-types';

const RANGES: DashboardRangeDays[] = [7, 30, 90];

export function DashboardGreetingHeader({
  dateLabel,
  greeting,
  rangeDays,
  onRangeChange,
  rangeLabels,
  rangeAriaLabel,
  notificationsLabel,
  unreadCount,
  onOpenNotifications,
  setupProductLabel,
  onSetupProduct,
  showSetupProduct,
  isMobile,
}: {
  dateLabel: string;
  greeting: string;
  rangeDays: DashboardRangeDays;
  onRangeChange: (days: DashboardRangeDays) => void;
  rangeLabels: Record<DashboardRangeDays, string>;
  rangeAriaLabel: string;
  notificationsLabel: string;
  unreadCount: number;
  onOpenNotifications: () => void;
  setupProductLabel: string;
  onSetupProduct: () => void;
  showSetupProduct: boolean;
  isMobile: boolean;
}) {
  const rangeControl = (
    <SegmentedControl
      ariaLabel={rangeAriaLabel}
      value={String(rangeDays) as `${DashboardRangeDays}`}
      onChange={(next) => onRangeChange(Number(next) as DashboardRangeDays)}
      options={RANGES.map((days) => ({
        value: String(days) as `${DashboardRangeDays}`,
        label: rangeLabels[days],
      }))}
      fullWidth={isMobile}
      itemClassName={isMobile ? 'min-h-11 h-[38px]' : undefined}
    />
  );

  const bell = (
    <Button
      type="button"
      variant="outline"
      size="icon"
      mode="icon"
      aria-label={notificationsLabel}
      className={cn(
        'relative shrink-0 rounded-xl',
        isMobile ? 'size-11' : 'size-10',
      )}
      onClick={onOpenNotifications}
    >
      <Bell className="size-[18px]" />
      {unreadCount > 0 ? (
        <span
          aria-hidden
          className="absolute right-2.5 top-2.5 size-2 rounded-full bg-[color:var(--brand-500)] ring-2 ring-card"
        />
      ) : null}
    </Button>
  );

  return (
    // max-md:pt-3 keeps the spacing of the hidden PageHeader title block that
    // used to sit above the greeting on mobile (DEV-537).
    <header className="flex flex-col gap-3 max-md:pt-3 md:gap-4">
      <div
        className={cn(
          'flex gap-3',
          isMobile ? 'items-center' : 'items-end gap-4',
        )}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-1 md:gap-1.5">
          <span className="text-[13px] text-muted-foreground">{dateLabel}</span>
          <h1
            id="dashboard-greeting-heading"
            className={cn(
              'text-balance font-bold tracking-[-0.4px] text-foreground',
              isMobile
                ? 'text-[27px] leading-[1.1]'
                : 'text-[34px] leading-[1.1] tracking-[-0.02em]',
            )}
          >
            {greeting}
          </h1>
        </div>
        {isMobile ? (
          bell
        ) : (
          <div className="flex shrink-0 items-center gap-2.5">
            {rangeControl}
            {bell}
            {showSetupProduct ? (
              <Button
                type="button"
                size="md"
                className="min-h-10 gap-2"
                onClick={onSetupProduct}
              >
                <Plus className="size-4" strokeWidth={2.25} />
                {setupProductLabel}
              </Button>
            ) : null}
          </div>
        )}
      </div>
      {isMobile ? rangeControl : null}
    </header>
  );
}
